import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { PageHeader, StatusBadge, money } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, PieChart, Pie, Tooltip,
} from "recharts";
import {
  ShoppingCart, ClipboardList, PackageCheck, ShieldCheck, Wrench, Recycle,
  IndianRupee, TrendingUp, LayoutDashboard, Bell, AlertTriangle, Warehouse,
} from "lucide-react";

const STORE_LABELS = {
  raw_wood: "Raw Wood", hardware: "Hardware", tools_machinery: "Tools & Machinery", misc: "Other Misc",
};

const DEPT_LABELS = {
  raw_wood_store: "Raw Wood", hardware_store: "Hardware", polish_store: "Polish",
  contract_worker: "Contractors", assembly: "Assembly", packing: "Packing",
};

function Kpi({ icon: Icon, label, value, sub, accent }) {
  const slug = String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  return (
    <Card data-testid={`kpi-${slug}`} className="animate-fade-up rounded-md border-border p-5 shadow-none transition-colors duration-200 hover:bg-muted/30">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
          <p className="mt-2 font-heading text-2xl font-bold tracking-tight text-foreground" data-testid={`kpi-value-${slug}`}>{value}</p>
          {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-md ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data)
    return (
      <div className="space-y-6">
        <PageHeader title="Dashboard" subtitle="Loading production overview…" icon={LayoutDashboard} />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-md" />)}
        </div>
      </div>
    );

  const k = data.kpis;
  const opsData = [
    { name: "Pending", value: data.operations_status.pending, fill: "#C28F3D" },
    { name: "In Progress", value: data.operations_status.in_progress, fill: "#4A5D6E" },
    { name: "Completed", value: data.operations_status.completed, fill: "#7A8B76" },
  ];
  const deptData = data.operations_by_dept.map((d) => ({
    name: DEPT_LABELS[d.department] || d.department, count: d.count,
  }));

  return (
    <div className="space-y-8">
      <PageHeader title="Production Dashboard"
        subtitle="Live overview of orders, operations, quality and dispatch." icon={LayoutDashboard} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
        <Kpi icon={ShoppingCart} label="Purchase Orders" value={k.purchase_orders}
          sub={money(k.po_value) + " ordered"} accent="bg-primary/10 text-primary" />
        <Kpi icon={ClipboardList} label="Active Operations" value={k.active_operations}
          sub="in progress / pending" accent="bg-[#4A5D6E]/10 text-[#4A5D6E]" />
        <Kpi icon={PackageCheck} label="In Production" value={k.products_in_production}
          sub="products on floor" accent="bg-ochre/10 text-ochre" />
        <Kpi icon={ShieldCheck} label="QC Pass Rate" value={(k.qc_pass + k.qc_reject) ? k.qc_pass_rate + "%" : "—"}
          sub={`${k.qc_pass} passed · ${k.qc_reject} rejected`} accent="bg-sage/15 text-sage" />
        <Kpi icon={IndianRupee} label="Avg Product Cost" value={money(k.avg_product_cost)}
          sub="per unit (costing)" accent="bg-primary/10 text-primary" />
        <Kpi icon={Recycle} label="Scrap Recorded" value={k.scrap_qty}
          sub={money(k.scrap_value) + " value"} accent="bg-destructive/10 text-destructive" />
        <Kpi icon={Wrench} label="Maintenance Due" value={k.maintenance_due}
          sub="assets need service" accent="bg-ochre/10 text-ochre" />
        <Kpi icon={TrendingUp} label="Total QC Checked" value={k.qc_pass + k.qc_reject}
          sub="units inspected" accent="bg-[#4A5D6E]/10 text-[#4A5D6E]" />
        <Kpi icon={Warehouse} label="Low Stock Items" value={k.low_stock_count ?? 0}
          sub="at / below minimum" accent="bg-destructive/10 text-destructive" />
        <Kpi icon={PackageCheck} label="Ready to Dispatch" value={k.dispatch_ready_count ?? 0}
          sub="all stages done" accent="bg-sage/15 text-sage" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="rounded-md border-border p-6 shadow-none lg:col-span-2">
          <h3 className="font-heading text-lg font-bold">Operations by Department</h3>
          <p className="mb-4 text-sm text-muted-foreground">Work orders dispatched to stores & contractors</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={deptData.length ? deptData : [{ name: "No data", count: 0 }]}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#5C5754" />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="#5C5754" />
                <Tooltip cursor={{ fill: "#8C3F2B10" }} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} fill="#8C3F2B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="rounded-md border-border p-6 shadow-none">
          <h3 className="font-heading text-lg font-bold">Operations Status</h3>
          <p className="mb-4 text-sm text-muted-foreground">Current workload split</p>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={opsData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {opsData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 space-y-1">
            {opsData.map((o) => (
              <div key={o.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: o.fill }} />{o.name}
                </span>
                <span className="font-semibold">{o.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="rounded-md border-border p-6 shadow-none">
        <h3 className="font-heading text-lg font-bold">Final Product Progress</h3>
        <p className="mb-5 text-sm text-muted-foreground">Tracking each order from raw wood to dispatch</p>
        <div className="space-y-5">
          {data.products.length === 0 && <p className="text-sm text-muted-foreground">No products yet.</p>}
          {data.products.map((p) => (
            <div key={p.id} data-testid={`dash-product-${p.id}`} className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="font-heading font-semibold">{p.name}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{p.sku} · {p.quantity} units</span>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={p.status} />
                  <span className="text-sm font-bold text-primary">{p.progress}%</span>
                </div>
              </div>
              <Progress value={p.progress} className="h-2" />
            </div>
          ))}
        </div>
      </Card>

      {data.rework_rollup?.length > 0 && (
        <Card className="rounded-md border-border p-6 shadow-none" data-testid="rework-rollup-card">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Recycle className="h-4 w-4 text-destructive" />
              <h3 className="font-heading text-lg font-bold">Rework & Reject Cost by SKU</h3>
            </div>
            <span className="text-sm text-muted-foreground">Est. total: <b className="text-destructive">{money(k.rework_cost)}</b></span>
          </div>
          <div className="overflow-x-auto rounded-md border border-border">
            <div className="grid min-w-[480px] grid-cols-5 bg-muted/50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span className="col-span-2">SKU / Product</span><span className="text-right text-ochre">Rework</span><span className="text-right text-destructive">Reject</span><span className="text-right">Est. cost</span>
            </div>
            {data.rework_rollup.map((r) => (
              <div key={r.sku} data-testid={`rework-row-${r.sku}`} className="grid min-w-[480px] grid-cols-5 border-t border-border px-4 py-2 text-sm">
                <span className="col-span-2 font-medium">{r.sku}<span className="ml-1 text-xs text-muted-foreground">{r.product_name}</span></span>
                <span className="text-right text-ochre">{r.rework}</span>
                <span className="text-right text-destructive">{r.reject}</span>
                <span className="text-right font-heading font-bold">{money(r.est_rework_cost)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {data.dispatch_ready?.length > 0 && (
        <Card className="rounded-md border-border p-6 shadow-none" data-testid="dispatch-ready-card">
          <div className="mb-4 flex items-center gap-2">
            <PackageCheck className="h-4 w-4 text-sage" />
            <h3 className="font-heading text-lg font-bold">Ready to Dispatch</h3>
            <span className="ml-auto text-sm text-muted-foreground">{data.dispatch_ready.length} product(s) — all stages done</span>
          </div>
          <div className="overflow-x-auto rounded-md border border-border">
            <div className="grid min-w-[480px] grid-cols-5 bg-muted/50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span className="col-span-2">Product</span><span>SKU</span><span>Buyer</span><span className="text-right">Qty</span>
            </div>
            {data.dispatch_ready.map((p) => (
              <div key={p.id} data-testid={`dispatch-ready-row-${p.id}`} className="grid min-w-[480px] grid-cols-5 border-t border-border px-4 py-2 text-sm">
                <span className="col-span-2 font-medium">{p.name}</span>
                <span className="text-muted-foreground">{p.sku || "—"}</span>
                <span className="text-muted-foreground">{p.buyer_name || "—"}</span>
                <span className="text-right font-heading font-bold text-sage">{p.quantity}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {data.low_stock?.length > 0 && (
        <Card className="rounded-md border-border p-6 shadow-none" data-testid="low-stock-card">
          <div className="mb-4 flex items-center gap-2">
            <Warehouse className="h-4 w-4 text-destructive" />
            <h3 className="font-heading text-lg font-bold">Low Stock Alerts</h3>
            <span className="ml-auto text-sm text-muted-foreground">{data.low_stock.length} item(s) need reordering</span>
          </div>
          <div className="overflow-x-auto rounded-md border border-border">
            <div className="grid min-w-[480px] grid-cols-5 bg-muted/50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span className="col-span-2">Item</span><span>Store</span><span className="text-right">Available</span><span className="text-right">Min</span>
            </div>
            {data.low_stock.map((s) => (
              <div key={s.id} data-testid={`low-stock-row-${s.id}`} className="grid min-w-[480px] grid-cols-5 border-t border-border px-4 py-2 text-sm">
                <span className="col-span-2 font-medium">{s.name}</span>
                <span className="text-muted-foreground">{STORE_LABELS[s.store] || s.store}</span>
                <span className="text-right font-heading font-bold text-destructive">{s.quantity} {s.unit}</span>
                <span className="text-right text-muted-foreground">{s.min_qty}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {data.maintenance_alerts?.length > 0 && (
        <Card className="rounded-md border-border p-6 shadow-none">
          <div className="mb-4 flex items-center gap-2">
            <Bell className="h-4 w-4 text-ochre" />
            <h3 className="font-heading text-lg font-bold">Maintenance Reminders</h3>
          </div>
          <div className="space-y-2">
            {data.maintenance_alerts.map((a) => (
              <div key={a.id} data-testid={`maint-alert-${a.id}`}
                className={`flex items-center justify-between rounded-md border px-4 py-3 ${a.level === "overdue" ? "border-destructive/30 bg-destructive/5" : "border-ochre/30 bg-ochre/5"}`}>
                <div className="flex items-center gap-3">
                  <AlertTriangle className={`h-4 w-4 ${a.level === "overdue" ? "text-destructive" : "text-ochre"}`} />
                  <div>
                    <p className="text-sm font-semibold">{a.asset_name} <span className="text-xs font-normal text-muted-foreground">{a.asset_code}</span></p>
                    <p className="text-xs text-muted-foreground">{a.reason}</p>
                  </div>
                </div>
                <StatusBadge status={a.level === "overdue" ? "out_of_order" : "needs_service"} />
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
